import { useState, useEffect, useCallback } from 'react';
import { Shield, Smartphone, AlertTriangle, LogIn, Globe, X, Check, Copy, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useAuth } from '../../contexts/AuthContext';

interface AuthEvent {
  EventId: string;
  EventType: string;
  CreationDate: string;
  EventResponse: string;
  EventRisk?: {
    RiskDecision: string;
    CompromisedCredentialsDetected: boolean;
  };
  ChallengeResponses?: {
    ChallengeName: string;
    ChallengeResponse: string;
  }[];
  EventContextData?: {
    IpAddress?: string;
    DeviceName?: string;
    City?: string;
    Country?: string;
  };
}

interface AuthActivityResponse {
  auth_events: AuthEvent[];
  next_token: string | null;
  user_id: string;
}

const SecuritySection = () => {
  const api = useAuthenticatedApi();
  const { getMFAStatus, setupTOTP, completeTOTPSetup, disableMFA } = useAuth();

  const [mfaStatus, setMfaStatus] = useState({ enabled: false, preferred: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isTogglingMfa, setIsTogglingMfa] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // MFA setup state - integrated into the main view
  const [isSettingUpMfa, setIsSettingUpMfa] = useState(false);
  const [setupData, setSetupData] = useState<{ secretKey?: string; qrCodeUri?: string }>({});
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const [authActivity, setAuthActivity] = useState<AuthEvent[]>([]);
  const [authActivityLoading, setAuthActivityLoading] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);

  // Fetch authentication activity data
  const fetchAuthActivity = useCallback(async () => {
    try {
      setAuthActivityLoading(true);
      const response = await api.get<AuthActivityResponse>('/auth/activity');
      setAuthActivity(response.auth_events);
    } catch (error) {
      console.error('Failed to fetch auth activity:', error);
    } finally {
      setAuthActivityLoading(false);
    }
  }, [api]);

  // Fetch security data on component mount
  useEffect(() => {
    const fetchSecurityData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch MFA status
        const mfaStatusResult = await getMFAStatus();
        setMfaStatus(mfaStatusResult);

        // Fetch authentication activity
        fetchAuthActivity();

      } catch (err) {
        console.error('Error loading security data:', err);
        setError('Failed to load security settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSecurityData();
  }, [getMFAStatus, fetchAuthActivity]);

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Reset confirmation state after timeout
  useEffect(() => {
    if (confirmDisable) {
      const timer = setTimeout(() => {
        setConfirmDisable(false);
      }, 3000); // Reset after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [confirmDisable]);

  // Handle MFA enable - smooth transition
  const handleMfaEnable = async () => {
    try {
      setError(null);
      setIsTogglingMfa(true);
      setIsSettingUpMfa(true);
      const setup = await setupTOTP();
      setSetupData(setup);
      setIsTogglingMfa(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set up MFA');
      setIsSettingUpMfa(false);
      setIsTogglingMfa(false);
    }
  };

  // Handle MFA disable
  const handleMfaDisable = async () => {
    // First click - show confirmation state
    if (!confirmDisable) {
      setConfirmDisable(true);
      return;
    }

    // Second click - actually disable
    try {
      setIsTogglingMfa(true);
      setConfirmDisable(false);
      await disableMFA();

      const updatedStatus = await getMFAStatus();
      setMfaStatus(updatedStatus);

      if (!updatedStatus.enabled) {
        setSuccessMessage('Two-factor authentication has been disabled');
      } else {
        setError('MFA could not be disabled. Please try again.');
      }
    } catch (err) {
      console.error('Error disabling MFA:', err);
      setError(err instanceof Error ? err.message : 'Failed to disable MFA');

      try {
        const currentStatus = await getMFAStatus();
        setMfaStatus(currentStatus);
      } catch { }
    } finally {
      setIsTogglingMfa(false);
      setConfirmDisable(false);
    }
  };

  // Handle MFA verification
  const handleMfaVerification = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    try {
      setIsVerifying(true);
      await completeTOTPSetup(verificationCode);

      const updatedStatus = await getMFAStatus();
      setMfaStatus(updatedStatus);

      if (updatedStatus.enabled) {
        setIsSettingUpMfa(false);
        setSetupData({});
        setVerificationCode('');
        setSuccessMessage('Two-factor authentication has been successfully enabled!');
      } else {
        setError('Failed to enable MFA. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');

      try {
        const currentStatus = await getMFAStatus();
        setMfaStatus(currentStatus);
      } catch { }
    } finally {
      setIsVerifying(false);
    }
  };

  // Cancel MFA setup
  const cancelMfaSetup = () => {
    setIsSettingUpMfa(false);
    setSetupData({});
    setVerificationCode('');
    setShowSecret(false);
    setSecretCopied(false);
    setError(null);
  };

  // Copy secret to clipboard
  const copySecret = async () => {
    if (setupData.secretKey) {
      try {
        await navigator.clipboard.writeText(setupData.secretKey);
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      } catch (err) {
        setError('Failed to copy to clipboard');
      }
    }
  };

  // Loading state for the entire section
  if (isLoading) {
    return (
      <div className="p-6">
        {/* Header skeleton */}
        <div className="mb-6">
          <div className="h-8 bg-ui-background-secondary dark:bg-neutral-800 rounded-md w-48 mb-2 animate-pulse"></div>
          <div className="h-4 bg-ui-background-secondary dark:bg-neutral-800 rounded-md w-80 animate-pulse"></div>
        </div>

        {/* 2FA Card skeleton */}
        <div className="mb-8 bg-ui-background-secondary dark:bg-neutral-800 rounded-lg border border-ui-border-primary p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-ui-background-tertiary dark:bg-neutral-700 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-5 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-48 mb-2"></div>
              <div className="h-4 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-64"></div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="h-4 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-40 mb-2"></div>
              <div className="h-3 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-56"></div>
            </div>
            <div className="h-10 w-24 bg-ui-background-tertiary dark:bg-neutral-700 rounded-lg"></div>
          </div>
        </div>

        {/* Activity skeleton */}
        <div>
          <div className="h-5 bg-ui-background-secondary dark:bg-neutral-800 rounded w-48 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 bg-ui-background-secondary dark:bg-neutral-800 rounded-lg border border-ui-border-primary animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-ui-background-tertiary dark:bg-neutral-700 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-48"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 animate-in fade-in duration-300">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">Security Settings</h2>
        <p className="text-neutral-600 dark:text-neutral-400">Manage your account security and privacy settings.</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Check size={20} className="text-green-600 dark:text-green-400 flex-shrink-0" />
            <p className="text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Two-Factor Authentication */}
        <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <Shield size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-ui-text-primary">Two-Factor Authentication</h3>
                <p className="text-sm text-ui-text-secondary">Add an extra layer of security to your account</p>
              </div>
            </div>

            {/* Status and toggle when not setting up */}
            {!isSettingUpMfa && (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-medium text-ui-text-primary">
                    Two-Factor Authentication {mfaStatus.enabled ? 'Enabled' : 'Disabled'}
                  </div>
                  <div className="text-sm text-ui-text-secondary">
                    {mfaStatus.enabled
                      ? 'Your account is protected with 2FA'
                      : 'Secure your account with 2FA using your phone'
                    }
                  </div>
                </div>
                <button
                  onClick={mfaStatus.enabled ? handleMfaDisable : handleMfaEnable}
                  disabled={isTogglingMfa}
                  className={`relative px-4 py-2 rounded-lg font-medium transition-all disabled:cursor-not-allowed ${confirmDisable
                      ? 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 animate-pulse'
                      : mfaStatus.enabled
                        ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30'
                        : 'bg-brand-primary text-black hover:bg-brand-primaryDark'
                    } ${isTogglingMfa ? 'pl-10' : ''}`}
                  data-tour="toggle-mfa-button"
                >
                  {isTogglingMfa && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  {isTogglingMfa ? 'Disabling...' : (
                    confirmDisable ? 'Click again to confirm' : (
                      mfaStatus.enabled ? 'Disable 2FA' : 'Enable 2FA'
                    )
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Integrated setup flow */}
          {isSettingUpMfa && setupData.qrCodeUri && (
            <div className="border-t border-ui-border-primary bg-ui-background-primary/50 animate-in slide-in-from-top duration-300">
              <div className="p-6 space-y-6">
                {/* Step indicator */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-brand-primary text-black rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                    <ChevronRight size={16} className="text-ui-text-tertiary" />
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${verificationCode.length === 6
                        ? 'bg-brand-primary text-black'
                        : 'bg-ui-background-tertiary text-ui-text-tertiary'
                      }`}>2</div>
                  </div>
                  <button
                    onClick={cancelMfaSetup}
                    className="text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                    data-tour="cancel-mfa-setup"
                  >
                    Cancel setup
                  </button>
                </div>

                {/* QR Code and secret */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-ui-text-primary mb-3">Scan QR Code</h4>
                    <p className="text-sm text-ui-text-secondary mb-4">
                      Use your authenticator app (Google Authenticator, Authy, etc.) to scan this code:
                    </p>
                    <div className="flex justify-center p-4 bg-white rounded-lg border border-ui-border-secondary">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qrCodeUri)}`}
                        alt="MFA QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-ui-text-primary mb-3">Manual Entry</h4>
                    <p className="text-sm text-ui-text-secondary mb-4">
                      Can't scan? Enter this code in your authenticator app:
                    </p>
                    <div className="space-y-3">
                      <div className="relative">
                        <input
                          type={showSecret ? "text" : "password"}
                          value={setupData.secretKey || ''}
                          readOnly
                          className="block w-full px-3 py-2 pr-20 border border-ui-border-primary rounded-md bg-ui-background-secondary text-ui-text-primary font-mono text-sm"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
                          <button
                            onClick={() => setShowSecret(!showSecret)}
                            className="p-1 hover:bg-ui-background-tertiary rounded transition-colors"
                            title={showSecret ? "Hide secret" : "Show secret"}
                          >
                            {showSecret ? <EyeOff size={16} className="text-ui-text-secondary" /> : <Eye size={16} className="text-ui-text-secondary" />}
                          </button>
                          <button
                            onClick={copySecret}
                            className="p-1 hover:bg-ui-background-tertiary rounded transition-colors"
                            title="Copy to clipboard"
                          >
                            {secretCopied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-ui-text-secondary" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Verification */}
                    <div className="mt-6">
                      <h4 className="font-medium text-ui-text-primary mb-3">Verify Setup</h4>
                      <p className="text-sm text-ui-text-secondary mb-4">
                        Enter the 6-digit code from your authenticator:
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          className="block flex-1 px-4 py-2 border border-ui-border-primary rounded-md bg-ui-background-primary text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-mono text-lg tracking-wider text-center"
                          autoFocus
                        />
                        <button
                          onClick={handleMfaVerification}
                          disabled={verificationCode.length !== 6 || isVerifying}
                          className="px-6 py-2 bg-brand-primary text-black rounded-md hover:bg-brand-primaryDark disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                          data-tour="verify-mfa-code"
                        >
                          {isVerifying ? 'Verifying...' : 'Verify'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Security Activity */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ui-text-primary">Recent Security Activity</h3>
            {authActivity.length > 3 && (
              <span className="text-sm text-ui-text-secondary">
                Showing {showAllActivity ? 'all' : '3 of'} {authActivity.length} events
              </span>
            )}
          </div>

          {authActivityLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 bg-ui-background-secondary dark:bg-neutral-800 rounded-lg border border-ui-border-primary animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-ui-background-tertiary dark:bg-neutral-700 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-4 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-24"></div>
                        <div className="h-3 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-12"></div>
                      </div>
                      <div className="h-3 bg-ui-background-tertiary dark:bg-neutral-700 rounded w-32"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : authActivity.length === 0 ? (
            <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary p-6">
              <p className="text-ui-text-secondary text-center">
                No recent security activity found.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {(showAllActivity ? authActivity : authActivity.slice(0, 3)).map((event) => {
                const eventDate = new Date(event.CreationDate);
                const formattedDate = eventDate.toLocaleString();

                let EventIcon = LogIn;
                let iconColor = "text-blue-600 dark:text-blue-400";
                let bgColor = "bg-blue-100 dark:bg-blue-900/20";

                if (event.EventType === "SignIn") {
                  EventIcon = LogIn;
                } else if (event.EventType === "PasswordChange") {
                  EventIcon = Shield;
                  iconColor = "text-green-600 dark:text-green-400";
                  bgColor = "bg-green-100 dark:bg-green-900/20";
                } else if (event.EventType === "ForgotPassword") {
                  EventIcon = Shield;
                  iconColor = "text-amber-600 dark:text-amber-400";
                  bgColor = "bg-amber-100 dark:bg-amber-900/20";
                } else {
                  EventIcon = Globe;
                }

                const statusColor = event.EventResponse === "Pass"
                  ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                  : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";

                return (
                  <div
                    key={event.EventId}
                    className="flex items-start p-4 bg-ui-background-secondary rounded-lg border border-ui-border-primary hover:border-ui-border-secondary transition-colors"
                  >
                    <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center mr-4 flex-shrink-0`}>
                      <EventIcon size={20} className={iconColor} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-ui-text-primary">
                          {event.EventType}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusColor}`}>
                          {event.EventResponse}
                        </span>
                      </div>

                      <p className="text-sm text-ui-text-secondary mb-1">
                        {formattedDate}
                      </p>

                      {event.EventContextData && (
                        <div className="mt-2 text-xs text-ui-text-secondary grid grid-cols-2 gap-x-4 gap-y-1">
                          {event.EventContextData.DeviceName && (
                            <div className="flex items-center gap-1">
                              <Smartphone size={12} />
                              <span className="truncate">{event.EventContextData.DeviceName}</span>
                            </div>
                          )}

                          {event.EventContextData.IpAddress && (
                            <div className="flex items-center gap-1">
                              <Globe size={12} />
                              <span>{event.EventContextData.IpAddress}</span>
                            </div>
                          )}

                          {event.EventContextData.City && event.EventContextData.Country && (
                            <div className="flex items-center gap-1 col-span-2">
                              <Globe size={12} />
                              <span>{event.EventContextData.City}, {event.EventContextData.Country}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {authActivity.length > 3 && (
                <button
                  onClick={() => setShowAllActivity(!showAllActivity)}
                  className="mt-2 w-full py-2 text-sm font-medium text-ui-text-secondary bg-ui-background-secondary hover:bg-ui-background-tertiary border border-ui-border-primary rounded-lg transition-colors"
                  data-tour="show-more-activity"
                >
                  {showAllActivity ? 'Show Less' : `Show ${authActivity.length - 3} More Events`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Security Recommendations */}
        {(() => {
          const recommendations = [];

          if (!mfaStatus.enabled && !isSettingUpMfa) {
            recommendations.push({
              id: 'enable-mfa',
              text: 'Enable two-factor authentication for enhanced security',
              priority: 'high',
            });
          }

          const hasUnusualLogins = authActivity.some(event =>
            event.EventType === 'SignIn' &&
            event.EventRisk?.RiskDecision === 'HighRisk'
          );

          if (hasUnusualLogins) {
            recommendations.push({
              id: 'review-logins',
              text: 'Unusual login activity detected. Review your recent logins and secure your account',
              priority: 'high'
            });
          }

          if (recommendations.length === 0) return null;

          return (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={20} className="text-amber-600" />
                  <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">Security Recommendations</h3>
                </div>
                {recommendations.length > 0 && (
                  <span className="px-2 py-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded-full">
                    {recommendations.length} {recommendations.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
              <ul className="space-y-3 text-sm text-amber-700 dark:text-amber-300">
                {recommendations.map(rec => (
                  <li key={rec.id} className="flex items-start">
                    <span className="mr-2">•</span>
                    <div className="flex-1">
                      <span>{rec.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SecuritySection;